import {
  ConversationMessage,
  ConversationEvent,
  InboxItem,
} from '../../../../../../core/src/lib/interface/conversation.interface';
import { MessageType } from '@vendasta/conversation';
import { DEFAULT_HOUR_GROUP_DURATION, DEFAULT_MESSAGE_GROUPING_DURATION } from '../../../../constants';
import { MessageGroup, MessageGroupable } from '../../../../../../core/src/lib/types';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class InboxItemGroupingService {
  canAddItemToPreviousGroup(item: InboxItem, previousGroup: MessageGroup): boolean {
    const lastItem = previousGroup?.lastItem;
    if (!lastItem) return false;

    const isSameType = item?.type === lastItem?.type;
    const isSameChannel = item?.channel === lastItem?.channel;
    const timeElapsed = (item?.created?.getTime() ?? 0) - (lastItem?.created?.getTime() ?? 0);
    const isWithinDuration = timeElapsed < DEFAULT_MESSAGE_GROUPING_DURATION;

    if (this.isConversationEvent(item) && this.isConversationEvent(lastItem)) {
      const isSameMessageFrom = item?.item.eventInfo.sentBy === lastItem?.item.eventInfo.sentBy;
      return isSameType && isSameChannel && isWithinDuration && isSameMessageFrom;
    }

    const isSameSender =
      this.isConversationMessage(item) && this.isConversationMessage(lastItem)
        ? item.item.sender?.participantId === lastItem.item.sender?.participantId
        : true;

    return isSameSender && isSameType && isSameChannel && isWithinDuration;
  }

  createNewGroupWithMessage(item: InboxItem, previousActivityDate: Date | undefined): MessageGroup {
    // if enough time has passed since the previous activity (message or event), set the createdAt
    // date of the new message group so a date divider will be shown in the conversation stream
    const previousDate = previousActivityDate;
    const messageDate = item.created;
    const enoughTimeElapsed =
      (messageDate?.getTime() ?? 0) - (previousDate?.getTime() ?? 0) > DEFAULT_HOUR_GROUP_DURATION;
    const addTimeDivider = !previousDate || enoughTimeElapsed;
    const createdAt = addTimeDivider ? messageDate : null;

    const newGroup = new MessageGroup(item.id, item.sender, item.channel, createdAt);
    newGroup.addItem(item);
    return newGroup;
  }

  groupItems(items?: InboxItem[]): MessageGroupable[] {
    const allItems = (items || []).filter(Boolean).sort((a, b) => {
      const aDate = a.created ? a.created.getTime() : 0;
      const bDate = b.created ? b.created.getTime() : 0;
      return aDate - bDate;
    });

    // Now, group the sorted items
    return allItems.reduce((list: MessageGroupable[], item) => {
      if (this.isSystemMessage(item) || this.isUntypedEvent(item)) {
        return [...list, item];
      }

      if (this.isConversationEvent(allItems[allItems.length - 1])) {
        (allItems[allItems.length - 1].item as ConversationEvent).isFirstItem = true;
      }

      // Check if we can add the item to the previous group
      const previousGroup = list[list.length - 1];

      if (previousGroup instanceof MessageGroup && this.canAddItemToPreviousGroup(item, previousGroup)) {
        previousGroup.addItem(item);
        return list;
      }

      const previousActivityDate =
        previousGroup && previousGroup instanceof MessageGroup ? previousGroup.lastItem.created : undefined;

      const newGroup = this.createNewGroupWithMessage(item, previousActivityDate);
      return [...list, newGroup];
    }, []);
  }

  isUntypedEvent(item: InboxItem): boolean {
    return this.isConversationEvent(item) && !item.type;
  }

  isConversationEvent(item: InboxItem): item is InboxItem & { item: ConversationEvent } {
    return item.item !== undefined && (item.item as ConversationEvent).event !== undefined;
  }

  isSystemMessage(item: InboxItem): boolean {
    return item.type === MessageType.MESSAGE_TYPE_SYSTEM;
  }

  isConversationMessage(item: InboxItem): item is InboxItem & { item: ConversationMessage } {
    return item.type === MessageType.MESSAGE_TYPE_MESSAGE;
  }
  isConversationTemplate(item: InboxItem): item is InboxItem & { item: ConversationMessage } {
    return item.type === MessageType.MESSAGE_TYPE_TEMPLATE;
  }
  isBookingAvailability(item: InboxItem): item is InboxItem & { item: ConversationMessage } {
    return item.type === MessageType.MESSAGE_TYPE_BOOKING_AVAILABILITY;
  }
}
